import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { toast } from "react-toastify";

//import components
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchVehiclesDocumentsData } from "../../store/vehicles-documents/actions";

const VehiclesDocumentsTable = props => {
    const dispatch = useDispatch();

    const { data, loading, error } = useSelector(state => state.vehiclesDocuments);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDriverDocuments, setSelectedDriverDocuments] = useState([]);
    const [selectedDocIds, setSelectedDocIds] = useState([]);

    const toggleDocSelection = (docId) => {
        setSelectedDocIds(prev =>
            prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
        );
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case "approved": return "success";
            case "rejected": return "danger";
            case "pending": return "warning";
            default: return "secondary";
        }
    };

    const isImage = (url) => {
        return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(url);
    };

    const getFileExtension = (url) => {
        return url?.split('.').pop() || 'file';
    };

    const handleViewDocuments = (driverRow) => {
        setSelectedDriverDocuments(driverRow || []);
        setSelectedDocIds([]);
        setModalOpen(true);
    };

    const handleActionClick = async (action, driverId, vehicleId) => {
        if (selectedDocIds.length === 0) {
            toast.error('Please select at least one document.', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        const payload = {
            action: action,
            document_ids: selectedDocIds,
            userId: driverId,
            vehicle_id: vehicleId
        };

        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(process.env.REACT_APP_WEB_APP_URL + "/api/web/update-vehicles-documents", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.status == 200) {
                toast.success('Documents update successfully', {
                    position: "top-right",
                    autoClose: 3000,
                });

                setModalOpen(false);
                dispatch(fetchVehiclesDocumentsData());
            } else {
                toast.error(response.statusText, {
                    position: "top-right",
                    autoClose: 3000,
                });
            }

        } catch (error) {
            console.error("Error updating document status:", error);
            toast.error('Failed to update documents.', {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    const { id: driverId } = useParams();
    useEffect(() => {
        dispatch(fetchVehiclesDocumentsData(driverId));
    }, [dispatch, driverId]);

    const columns = useMemo(
        () => [
            {
                header: 'Vehicle',
                accessorKey: 'vehicle',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Action',
                id: 'action',
                cell: ({ row }) => {
                    return (
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleViewDocuments(row.original)}
                        >
                            <i className="mdi mdi-eye"></i> View
                        </button>
                    );
                }
            }
        ],
        []
    );

    const totalRecords = data?.data?.length ?? 0;

    const transformedData = useMemo(() => {
        return data?.data?.map(item => {
            return {
                vehicle: item.vehicle_make_name + ' - ' + item.vehicle_license_plate,
                ...item
            };
        });
    }, [data]);

    return (
        <>
            {loading ? (
                <Skeleton count={10} height={40} />
            ) : (
                <TableContainer
                    columns={columns}
                    data={transformedData || []}
                    isGlobalFilter={true}
                    isPagination={true}
                    SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
                    pagination="pagination"
                    paginationWrapper='dataTables_paginate paging_simple_numbers'
                    tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                />
            )}

            {modalOpen && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Vehicle Documents</h5>
                                <button type="button" className="btn-close" onClick={() => setModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex flex-wrap gap-3">
                                    {selectedDriverDocuments.documents.map(doc => {
                                        const isApproved = doc.status?.toLowerCase() === 'approved';
                                        const isSelected = selectedDocIds.includes(doc.id);

                                        return (
                                            <div
                                                key={doc.id}
                                                className={`card p-2 ${isSelected ? 'border border-success' : ''} ${isApproved ? 'opacity-50' : ''}`}
                                                style={{ width: "180px", cursor: isApproved ? "not-allowed" : "pointer", position: "relative" }}
                                                onClick={() => {
                                                    if (!isApproved) toggleDocSelection(doc.id);
                                                }}
                                            >
                                                {
                                                    isImage(doc.document) ? (
                                                        <img
                                                            src={doc.document}
                                                            alt={doc.document_name}
                                                            style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "6px" }}
                                                        />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                width: "100%",
                                                                height: "100px",
                                                                borderRadius: "6px",
                                                                backgroundColor: "#f0f0f0",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "14px",
                                                                color: "#333"
                                                            }}
                                                        >
                                                            {getFileExtension(doc.document).toUpperCase()} File
                                                        </div>
                                                    )
                                                }
                                                <div className="mt-2 text-center fw-semibold">{doc.document_name}</div>

                                                {/* Status Badge */}
                                                <div className={`badge bg-${getStatusColor(doc.status)} position-absolute top-0 end-0 m-1`}>
                                                    {doc.status}
                                                </div>

                                                {/* View Button */}
                                                <button
                                                    className="btn btn-sm btn-outline-primary mt-2"
                                                    style={{ width: "100%" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(doc.document, '_blank');
                                                    }}
                                                >
                                                    View
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* <div className="modal-footer">
                                <button className="btn btn-success" onClick={() => handleActionClick("approved", selectedDriverDocuments.driver_id, selectedDriverDocuments.vehicle_id)}>Approved</button>
                                <button className="btn btn-danger" onClick={() => handleActionClick("rejected", selectedDriverDocuments.driver_id, selectedDriverDocuments.vehicle_id)}>Reject</button>
                            </div> */}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

VehiclesDocumentsTable.propTypes = {
    t: PropTypes.any
};

export default VehiclesDocumentsTable;